// Collapsible panel-notes affordance (dashboard-agent-removal spec §3.2):
// every chart/table panel can carry 2-3 sentences of plain prose behind a
// "NOTES" pill in its title bar, collapsed by default.
//
// Three pieces:
//   - NotesPillButton: small "NOTES" pill styled like the CompletenessBadge
//     warning variant (amber outline, uppercase, rounded-full) so the two
//     title-bar pills read as one family. A TRUE <button> — callers must
//     render it as a sibling of any collapse trigger, never nested inside one.
//   - NotesBody: the collapsible muted-prose block, animated with the same
//     height/opacity idiom as DashboardCard's "See more" detail block
//     (framer-motion height 0→auto, DURATION.medium/EASE.out in,
//     DURATION.short/EASE.in out, reduced-motion honored).
//   - FlatPanelNotes: wrapper for panels inside tab_group tabs / row-detail
//     sheets, which render without a SectionShell/title bar — gives them a
//     minimal title row so the pill has an anchor.

import { useId, useState, type ReactNode } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "@/shared/lib/utils";
import { DURATION, EASE } from "./motion";

export function NotesPillButton({
  open,
  onToggle,
  controlsId,
}: {
  open: boolean;
  onToggle: () => void;
  controlsId: string;
}) {
  return (
    <button
      type="button"
      aria-expanded={open}
      aria-controls={controlsId}
      onClick={onToggle}
      className={cn(
        // Exact CompletenessBadge (warning tone) pill styling.
        "inline-flex items-center gap-0.5 rounded-full border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide",
        "border-status-warning/40 bg-status-warning/10 text-status-warning",
        "cursor-pointer select-none hover:bg-status-warning/20 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-status-warning/60",
      )}
    >
      Notes
      <ChevronDown
        className={cn(
          "h-2.5 w-2.5 transition-transform duration-200",
          open ? "rotate-0" : "-rotate-90",
        )}
      />
    </button>
  );
}

export function NotesBody({ id, open, body }: { id: string; open: boolean; body: string }) {
  const prefersReduced = useReducedMotion();
  return (
    <AnimatePresence initial={false}>
      {open ? (
        <motion.div
          key="notes"
          id={id}
          initial={{ height: 0, opacity: 0 }}
          animate={{
            height: "auto",
            opacity: 1,
            transition: prefersReduced
              ? { duration: 0 }
              : { duration: DURATION.medium, ease: EASE.out },
          }}
          exit={{
            height: 0,
            opacity: 0,
            transition: prefersReduced
              ? { duration: 0 }
              : { duration: DURATION.short, ease: EASE.in },
          }}
          style={{ overflow: "hidden" }}
        >
          <div className="border-t border-border bg-muted/20 px-5 py-3 text-xs leading-relaxed text-muted-foreground [&_strong]:text-foreground/80">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

/** Notes frame for shell-less contexts (tab_group tabs / row_detail): a thin
 *  title row carrying the pill, the collapsible block, then the panel body.
 *  Side effect ratified in spec §3.3: in-tab titles display when (and only
 *  when) the panel carries notes. */
export function FlatPanelNotes({
  title,
  notes,
  children,
}: {
  title?: string;
  notes: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const notesId = useId();
  return (
    <div>
      <div className="flex items-baseline gap-3 px-5 pt-3.5 pb-1">
        {title ? (
          <h4 className="truncate text-sm font-semibold tracking-tight">{title}</h4>
        ) : null}
        <NotesPillButton
          open={open}
          onToggle={() => setOpen((o) => !o)}
          controlsId={notesId}
        />
      </div>
      <NotesBody id={notesId} open={open} body={notes} />
      {children}
    </div>
  );
}
